import * as R from 'ramda'

import {
  changePhoneNumber,
  createOrder,
  getRedirectUrl,
  reducer,
  transitionMachine,
} from './reducer'
import {useEffect, useMemo, useReducer, useRef, useState} from 'react'

import {Api} from './api'
import {ORDER_METHODS} from './constants'
import format from 'date-fns/format'
import kebabCase from 'lodash.kebabcase'

function toError(error) {
  if (error) {
    try {
      const errObj = JSON.parse(error.replace(/(=>)/g, ':'))
      const errorCode = R.pathOr(null, ['base', 0, 'errorCode'], errObj)
      const errorReason = R.pathOr(null, ['base', 0, 'reason'], errObj)
      const expiresAt = format(
        R.pathOr('', ['base', 0, 'expiresAt'], errObj),
        'DD.MM.YYYY'
      )

      return {
        message:
          'projs.errors.' +
          (errorCode && errorReason
            ? `${errorCode}.${errorReason}`
            : 'login-failed'),
        variables: {expiresAt},
      }
    } catch (e) {
      return {
        message: 'projs.errors.login-failed',
      }
    }
  }
  return {
    message: 'projs.errors.login-failed',
  }
}
/**
 * @typedef {Object} OrderRenderProps
 * @property {?string} id Unique order ID
 * @property {string} messageText Status message text as translation key
 * @property {("info"|"error")} messageType Status message type
 * @property {?string} qrCode QR code data URI
 * @property {?string} redirectUrl URL to redirect user to
 * @property {string} status Order state machine status
 * @property {?(string): void} onPaymentStart Start payment callback, takes phone number
 */

/**
 * Create order and keep track of its lifecycle
 * @returns {OrderRenderProps}
 */
export function useOrder(params) {
  // Create API instance once in component’s lifetime
  const api = useMemo(() => new Api(params.apiUrl), [params.apiUrl])

  const [state, dispatch] = useReducer(reducer, {
    id: params.orderId || null,
    method: null,
    machinePayload: {},
    machineState: 'pending',
    phoneNumber: null,
  })

  const redirectUrl = getRedirectUrl(state, {
    callbackUrl: params.url,
    lastState: params.lastState,
  })

  const prevMachineStateRef = useRef(params.lastState)
  useEffect(() => {
    if (prevMachineStateRef.current !== state.machineState) {
      params.onStateChange(
        prevMachineStateRef.current,
        state.machineState,
        state.machinePayload
      )
      prevMachineStateRef.current = state.machineState
    }
  }, [state.machinePayload, state.machineState])

  // As soon as component using this hook gets mounted an attemp to create an
  // order is made.
  useEffect(() => {
    if (!state.id) {
      api
        .createOrder(params)
        .then((body) => {
          dispatch(createOrder(body.order_id))
        })
        .catch((error) => {
          dispatch(transitionMachine('fail', {error}))
        })
    }
  }, [dispatch, params, state.id])

  useEffect(() => {
    if (redirectUrl) {
      params.onRedirect(redirectUrl)
    }
  }, [params.onRedirect, redirectUrl])

  useEffect(() => {
    if (
      state.machineState === 'starting_payment' &&
      (params.orderMethod === ORDER_METHODS.local || state.phoneNumber)
    ) {
      api.startPayment(state.id, params.orderMethod, state.phoneNumber)
    }
  }, [params.orderMethod, state.id, state.machineState, state.phoneNumber])

  const [attempts, setAttempts] = useState(10)
  useEffect(() => {
    if (
      state.id &&
      state.machineState !== 'completed' &&
      state.machineState !== 'failed'
    ) {
      const intervalId = setInterval(() => {
        api
          .status(state.id)
          .then((data) => {
            setAttempts(10)
            dispatch(transitionMachine(data.action, data.action_payload))
          })
          .catch((error) => {
            if (attempts < 0) {
              dispatch(transitionMachine('fail', {error}))
            } else {
              setAttempts((attempts) => attempts - 1)
            }
          })
      }, 1000)
      return () => clearInterval(intervalId)
    }
  }, [state.id, state.machineState, dispatch])

  useEffect(() => {
    if (state.machineState === 'completed') {
      params.onSuccess(state.machinePayload.token)
    } else if (state.machineState === 'failed') {
      params.onFailure(
        toError(R.pathOr([], ['machinePayload', 'errors', 0, 'reason'], state))
      )
    }
  }, [
    params.onSuccess,
    params.onFailure,
    state.machineState,
    state.machinePayload,
  ])

  return {
    id: state.id,
    messageText: `projs.status.${kebabCase(state.machineState)}`,
    messageType: state.machineState === 'failed' ? 'error' : 'info',
    qrCode: state.machinePayload.qr_code,
    redirectUrl,
    status: state.machineState,
    onPaymentStart:
      params.orderMethod !== ORDER_METHODS.local &&
      state.machineState === 'starting_payment'
        ? (phoneNumber) => dispatch(changePhoneNumber(phoneNumber))
        : null,
  }
}
